import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConnectorStore } from '@/lib/connectors/store';
import {
  Plus,
  Plug,
  MoreVertical,
  Edit,
  Trash2,
  Search,
} from 'lucide-react';

export const Route = createFileRoute('/_dashboard/connectors/')({
  component: ConnectorsPage,
});

function ConnectorsPage() {
  const navigate = useNavigate();
  const { connectors, connectorsLoading, loadConnectors, deleteConnector } = useConnectorStore();
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadConnectors();
  }, [loadConnectors]);

  const filteredConnectors = connectors.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.description?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Delete connector "${name}"?`)) {
      await deleteConnector(id);
    }
  };

  if (connectorsLoading && connectors.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Plug className="size-6" />
            Custom Connectors
          </h1>
          <p className="text-muted-foreground mt-1">
            Build and manage custom API connectors
          </p>
        </div>
        <Button onClick={() => navigate({ to: '/connectors/new' })}>
          <Plus className="size-4 mr-2" />
          New Connector
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search connectors..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Connectors Grid */}
      {filteredConnectors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Plug className="size-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">
              {search ? 'No matching connectors' : 'No custom connectors'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {search
                ? 'Try a different search term'
                : 'Create your first custom connector to integrate with any API'}
            </p>
            {!search && (
              <Button onClick={() => navigate({ to: '/connectors/new' })}>
                <Plus className="size-4 mr-2" />
                Create Connector
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredConnectors.map((connector) => (
            <Card
              key={connector.id}
              className="group hover:shadow-md transition-shadow cursor-pointer"
              onClick={() =>
                navigate({ to: '/connectors/$connectorId', params: { connectorId: connector.id } })
              }
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {connector.icon_url ? (
                      <img
                        src={connector.icon_url}
                        alt=""
                        className="size-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Plug className="size-5 text-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium">
                        {connector.display_name || connector.name}
                      </h3>
                      <span className="text-xs text-muted-foreground">
                        v{connector.version}
                      </span>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100"
                      >
                        <MoreVertical className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate({ to: '/connectors/$connectorId', params: { connectorId: connector.id } });
                        }}
                      >
                        <Edit className="size-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(connector.id, connector.name);
                        }}
                      >
                        <Trash2 className="size-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <p className="text-sm text-muted-foreground line-clamp-2">
                  {connector.description || 'No description'}
                </p>

                <div className="flex items-center gap-2 mt-4">
                  <Badge variant="secondary" className="capitalize">
                    {connector.auth_type}
                  </Badge>
                  {connector.is_published && (
                    <Badge variant="default">Published</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
